/* eslint-disable react/prop-types */
import { useState } from 'react';
import { DatePicker, Select, Button, Tooltip, Grid } from 'antd';
import {  DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  DebouncedSelect,
  fetchChargerList,
  fetchLocationList,
} from '../../components/useDebouncedSelectLocation';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { useBreakpoint } = Grid;

export default function SharedFilters({
  operatorId,
  onFiltersChange,
  onExport,
  isLoading,
  showRfidFilter = true,
}) {
  const { t } = useTranslation(['reportPage', 'common']);
  const { t: tCommon } = useTranslation('common');
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
    dayjs().format('YYYY-MM-DD'),
  ]);
  const [rfidFilter, setRfidFilter] = useState('ALL');
  const [chargerId, setChargerId] = useState(null);
  const [locationId, setLocationId] = useState(null);

  const datePresets = [
    { label: tCommon('today'), value: [dayjs(), dayjs()] },
    { label: tCommon('yesterday'), value: [dayjs().add(-1, 'd'), dayjs().add(-1, 'd')] },
    { label: tCommon('last7Days'), value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: tCommon('last30Days'), value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: tCommon('thisMonth'), value: [dayjs().startOf('month'), dayjs().endOf('month')] },
    { label: tCommon('lastMonth'), value: [dayjs().add(-1, 'month').startOf('month'), dayjs().add(-1, 'month').endOf('month')] },
  ];

  const handleDateChange = (dates) => {
    const formattedDates = dates
      ? [
          dayjs(dates[0]).format('YYYY-MM-DD'),
          dayjs(dates[1]).format('YYYY-MM-DD'),
        ]
      : [];
    setDateRange(formattedDates);
    onFiltersChange({
      dateRange: formattedDates,
      rfidFilter,
      chargerId,
      locationId,
    });
  };

  const handleFilterChange = (key, value) => {
    const updatedFilters = {
      dateRange,
      rfidFilter,
      chargerId,
      locationId,
      [key]: value,
    };
    
    if (key === 'rfidFilter') setRfidFilter(value);
    if (key === 'locationId') {
      setLocationId(value);
      // Clear charger when location changes since charger options depend on location
      setChargerId(null);
      updatedFilters.chargerId = null;
    }
    if (key === 'chargerId') setChargerId(value);
    
    onFiltersChange(updatedFilters);
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      dateRange: [
        dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD'),
      ],
      rfidFilter: 'ALL',
      chargerId: null,
      locationId: null,
    };
    setDateRange(defaultFilters.dateRange);
    setRfidFilter(defaultFilters.rfidFilter);
    setChargerId(defaultFilters.chargerId);
    setLocationId(defaultFilters.locationId);
    onFiltersChange(defaultFilters);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center mb-4">
      <RangePicker
        onChange={handleDateChange}
        className={`${isMobile ? 'w-full' : 'w-full min-w-[250px]'}`}
        value={
          dateRange.length === 2
            ? [dayjs(dateRange[0]), dayjs(dateRange[1])]
            : null
        }
        presets={datePresets}
      />
  {showRfidFilter &&
      <Select
        value={rfidFilter}
        onChange={(value) => handleFilterChange('rfidFilter', value || 'ALL')}
        className={`${isMobile ? 'w-full' : 'w-full min-w-[150px]'}`}
        allowClear
      >
        <Option value="ALL">{t('All')}</Option>
        <Option value="RFID">{t('rfidOnly')}</Option>
        <Option value="NO_RFID">{t('withoutRfid')}</Option>
      </Select>}
      {operatorId && (
        <>
         <DebouncedSelect
            value={locationId}
            onChange={(value) => handleFilterChange('locationId', value)}
            operatorId={operatorId}
            allowClear
            className={`${isMobile ? 'w-full' : 'w-full min-w-[250px]'}`}
            fetchOptions={(search) => fetchLocationList({ operatorId, search })}
            placeholder={t('searchLocation')}
          />
          <DebouncedSelect
            value={chargerId}
            onChange={(value) => handleFilterChange('chargerId', value)}
            operatorId={operatorId}
            allowClear
            className={`${isMobile ? 'w-full' : 'w-full min-w-[250px]'}`}
            fetchOptions={(search) => fetchChargerList({ operatorId, search, locationId })}
            placeholder={t('searchCharger')}
          />
         
        </>
      )}
     
      <div className="flex gap-2 w-full lg:w-auto">
        <Button 
          onClick={handleClearFilters} 
          disabled={isLoading}
          className={isMobile ? 'flex-1' : ''}
        >
          {t('clear')}
        </Button>
        <Tooltip title={t('exportCsv')}>
          <Button
            icon={<DownloadOutlined />}
            onClick={onExport}
            disabled={isLoading}
            className={isMobile ? 'flex-1' : ''}
          />
        </Tooltip>
      </div>
    </div>
  );
}

SharedFilters.propTypes = {
  operatorId: PropTypes.string,
  onFiltersChange: PropTypes.func.isRequired,
  onExport: PropTypes.func,
  isLoading: PropTypes.bool,
  showRfidFilter: PropTypes.bool,
};
